// import { updateShippingMethod, updateShippingResume } from "./shippingCTUtils";
import { scrollAnimate, stopAnimationOnScroll } from "../components/scrollAnimate";
import { reInitVisualSteps } from "./visualSteps";
import { scrollToElement } from "../utilities/scrollToElement";
import { parents } from "../utilities/selectorHelper";

/**
 * Return if step has errors based on is-invalid class DOM elements
 * @param {string} step - step id
 */
const hasStepErrors = step => {
    const stepElement = document.getElementById(step);
    const hasErrors = stepElement.getElementsByClassName("is-invalid").length > 0 || stepElement.classList.contains("js-step-required") ? "true" : "false";

    return hasErrors;
};

/**
 * Update step that will be disabled
 * @param {Object} step - step that will be disabled
 * @param {Boolean} collapseStep - collapse or not the current step
 */
const disableStep = (step, collapseStep = false) => {
    if (!step) { return; }

    if (step.id != "step-payment-nonfinancial" && step.id != "step-payment-methods" && collapseStep) {
        const stepBodyResume = step.querySelector(".js-step-body-resume");
        const stepBodySelection = step.querySelector(".js-step-body-selection");

        if (stepBodyResume) {
            stepBodyResume.classList.remove("d-none");
            stepBodyResume.classList.remove("d-sm-none");
        }

        if (stepBodySelection && step.classList.contains("step-type--address")) {
            stepBodySelection.classList.add("d-none");
        }
    }

    document.dispatchEvent(new CustomEvent("checkoutSteps.disabledStep", {detail: step}));
};

/**
 * Handles the transition from one type of address to the other -
 * styling and events loaded for the new type of address
 * @param {Object} step - step that will be active one */
const toggleDifferentTypeAddress = (step) => {
    if (!step) { return; }

    const stepHeaderButtons = step.querySelectorAll(".button-shipping-selector");
    stepHeaderButtons.forEach (function (button) {
        button.classList.toggle("js-step-header-active");
        button.classList.toggle("selected");
        button.classList.toggle("unselected");
        button.querySelector(".svg-icon-checkmark")?.classList.toggle("d-none");
        const toggleValue = button.getAttribute("data-toggle") === "modal"; 
        button.setAttribute("data-toggle", toggleValue === "modal" ? "none" : "modal");
        if (button.id == "receive-address") button.classList.toggle("js-action-change-address");
    });
};

/**
 * Update clicked step that will be the active one
 * @param {Object} step - step that will be active one
 * @param {Boolean} skipScroll - boolean to skip scrolling to the enabled step
 */
const enableStep = (step, skipScroll = false) => {
    if (!step) { return; }

    step.classList.add("js-step-active");

    if (step.id != "step-payment-nonfinancial" && step.id != "step-payment-methods") {
        const stepBodyResume = step.querySelector(".js-step-body-resume");
        const stepBodySelection = step.querySelector(".js-step-body-selection");

        if (stepBodyResume) {
            stepBodyResume.classList.add("d-sm-none");
            stepBodyResume.classList.add("d-none");
        }

        if (stepBodySelection) {
            const stepActive = step.querySelector(".js-step-header-active")?.id;
            //verify if expansion resume has the selected address checked
            const inputValue = step.getAttribute("data-selected-delivery");
            const inputElement = stepBodySelection.querySelector(`input[value="${inputValue}"]`);
            if (inputElement && !inputElement.checked) {
                inputElement.checked = true;
            }

            switch (stepActive) {
                case "receive-address":
                    step.querySelector(".js-shipping-hd-addresses")?.classList.remove("d-none");
                    step.querySelector(".js-stores-container-shipment")?.classList.add("d-none");
                    break;
                case "receive-store":
                    step.querySelector(".js-stores-container-shipment")?.classList.remove("d-none");
                    step.querySelector(".js-shipping-hd-addresses")?.classList.add("d-none");
                    break;
            }

            stepBodySelection.classList.remove("d-none");
        }
    }

    if (!skipScroll) {
        scrollToElement($(step));
    }
    document.dispatchEvent(new CustomEvent("checkoutSteps.enabledStep", {detail: step}));
};

/**
 * Disable a step and go to the next step
 * @param {Object} step - active step to disable
 * @param {Boolean} skipScroll - boolean to skip scrolling to the enabled step
 */
const disableAndNextStep = (step, target, skipScroll = false) => {
    if (!step || step === null) {
        const [scheduleStep] = parents(target, ".step-type--schedule");

        if (scheduleStep && scheduleStep.length === 0) { return; }
        step = scheduleStep;
    }

    disableStep(step, true);
    step.classList.remove("js-step-required");
    step.classList.add("js-step-filled");

    const nextStep = document.querySelector(".step--wrapper:not(.js-step-filled)");
    enableStep(nextStep, skipScroll);
};

/**
 * Sets the filled state on the step
 * @param {Object} step - active step to add the filled state
 */
const stepFilled = step => {
    if (!step) { return; }
    step.classList.remove("js-step-required");
    step.classList.add("js-step-filled");

    document.dispatchEvent(new CustomEvent("checkoutSteps.filledStep", {detail: step}));
};

/**
 * Sets the unfilled state on the step
 * @param {Object} step - active step to remove the filled state
 */
const stepUnfilled = step => {
    if (!step) { return; }
    step.classList.remove("js-step-filled");

    document.dispatchEvent(new CustomEvent("checkoutSteps.unfilledStep", {detail: step}));
};

/**
 * Handle step editing
 * Check if active step has errors displayed and scroll to the step if any
 * If no errors on active step, collapse it and activate the clicked one to edit it
 */
const editStepHandler = () => {
    document.querySelectorAll(".js-step-edit-address").forEach(wrapper => {
        wrapper.addEventListener("click", e => {
            const edit = e.currentTarget;
            const editStep = edit.closest(".js-step-wrapper");
            const activeStep = document.querySelector(".js-step-active");
            const activeStepId = activeStep?.getAttribute("id");

            if (edit.classList.contains("js-step-active")) { return; }

            if (activeStepId) {
                if (hasStepErrors(activeStepId) == "true") {
                    // To DO - include sticky header height when scroll to the top of the step
                    scrollAnimate($("#" + activeStepId));
                    return;
                }

                const isScheduleSelected = [...activeStep.querySelectorAll(".interval-item-wrapper")].some(item => item.classList.contains("selected"));

                // disable active step
                disableStep(activeStep, !activeStep.classList.contains("step-type--schedule") || isScheduleSelected);
            }

            // enable clicked step
            enableStep(editStep);
            editStep.classList.remove("js-step-filled");

            reInitVisualSteps();

            if (window.mqRespond.status.mobile) {
                const tooltipID = edit.getAttribute("aria-describedby");
                const editStepTooltip = document.getElementById(tooltipID);

                if (editStepTooltip) { editStepTooltip.remove(); }
            }
        });
    });
};

/**
 * Handle next step
 * Collapse current step if valid based on js-step-active class
 * Expand next step based on js-step-filled class
 */
const nextStepHandler = () => {
    // Event handlers to activate next step in DOM that doesn't have js-step-filled class
    document.querySelectorAll(".step-type--address .pwc-form-check-input").forEach(radioInput => {
        radioInput.addEventListener("click", e => {
            const clickedDeliveryRadio = e.currentTarget;

            if (!clickedDeliveryRadio.closest(".js-step-active")) { return; }

            const activeStep = clickedDeliveryRadio.closest(".js-step-active");
            const selectedDeliveryID = activeStep.getAttribute("data-selected-delivery");

            if (clickedDeliveryRadio.getAttribute("id") == selectedDeliveryID) {
                disableAndNextStep(activeStep);
            }

            reInitVisualSteps();
        });
    });
};

/**
 * Gets current available steps from the page and their state
 * @returns {object} Object containing steps
 */
const getAvailableSteps = () => {
    const steps = {};
    const availableSteps = document.querySelectorAll(".js-step-dot");
    const availableFilledSteps = document.querySelectorAll(".js-step-dot.js-step-filled");

    if (!availableSteps) { return; }
    const allSteps = Array.from(availableSteps);
    const activeStep = document.querySelector(".js-step-dot.js-step-active");

    steps.allStepsIds = Array.from(availableSteps).map(element => element.getAttribute("id"));
    steps.activeStepId = activeStep?.getAttribute("id");
    steps.previousStepId = allSteps[allSteps.indexOf(activeStep) - 1]?.getAttribute("id");
    steps.nextStepId = allSteps[allSteps.indexOf(activeStep) + 1]?.getAttribute("id");

    if (availableFilledSteps) {
        steps.filledStepsIds = Array.from(availableFilledSteps).map(element => element.getAttribute("id"));
    }

    return steps;
};

/**
 * Step custom events listeners
 */
const stepsCustomEvents = () => {
    document.addEventListener("checkoutSteps.disabledStep", () => {
        localStorage.setItem("availableSteps", JSON.stringify(getAvailableSteps()));
    });

    document.addEventListener("checkoutSteps.enabledStep", () => {
        localStorage.setItem("availableSteps", JSON.stringify(getAvailableSteps()));
    });

    document.addEventListener("checkoutSteps.filledStep", () => {
        localStorage.setItem("availableSteps", JSON.stringify(getAvailableSteps()));
    });

    document.addEventListener("checkoutSteps.unfilledStep", () => {
        localStorage.setItem("availableSteps", JSON.stringify(getAvailableSteps()));
    });
};


/**
 * Helper function to get the available steps item from localStorage
 * Then parse it into object
 * @returns {object} Object containing available steps as Nodes
 */
const parseAvailableStepsObject = () => {
    const availableStepsString = localStorage.getItem("availableSteps");
    if (!availableStepsString) { return; }
    const stepsObjectIds = JSON.parse(availableStepsString);
    let stepsObjectNodes = {};

    stepsObjectNodes.activeStep = document.getElementById(stepsObjectIds.activeStepId);
    stepsObjectNodes.allSteps = stepsObjectIds.allStepsIds.map(element => document.getElementById(element));
    stepsObjectNodes.filledSteps = stepsObjectIds.filledStepsIds.map(element => document.getElementById(element));
    stepsObjectNodes.nextStep = document.getElementById(stepsObjectIds.nextStepId);
    stepsObjectNodes.previousStep = document.getElementById(stepsObjectIds.previousStepId);

    return stepsObjectNodes;
};

/**
 * Init steps events
 */
const initStepsEvents = () => {
    editStepHandler();
    nextStepHandler();
    stepsCustomEvents();
    stopAnimationOnScroll();
};

export {
    initStepsEvents,
    editStepHandler,
    disableAndNextStep,
    stepFilled,
    stepUnfilled,
    disableStep,
    enableStep,
    getAvailableSteps,
    parseAvailableStepsObject,
    toggleDifferentTypeAddress
};
